import React, { useState, useEffect } from 'react';
import { UserCheck, Plus, Search, Edit, Eye, CheckCircle, XCircle, Calendar, ArrowUpDown, ArrowUp, ArrowDown, Filter, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { firebaseService, Turma } from '../services/firebaseService';
import { User } from '../types/User';
import { useNotification } from '../hooks/useNotification';
import ProfessorModal from '../components/ProfessorModal';

const Professores: React.FC = () => {
  const { userProfile } = useAuth();
  const { showSuccess, showError, showConfirm, showInfo, NotificationComponent } = useNotification();
  const [professores, setProfessores] = useState<User[]>([]);
  const [turmas, setTurmas] = useState<Turma[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<'nome' | 'email' | 'status'>('nome');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedProfessor, setSelectedProfessor] = useState<User | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showProfessorModal, setShowProfessorModal] = useState(false);
  const [selectedTurma, setSelectedTurma] = useState<Turma | null>(null);
  const [showTurmaModal, setShowTurmaModal] = useState(false);

  // Carregar todos os dados do Firebase de forma otimizada
  useEffect(() => {
    const loadData = async () => {
      if (!userProfile?.empresaCnpj) return;

      try {
        setLoading(true);
        setError(null);
        
        // Carregar todos os dados de uma vez (otimizado)
        const { professores: professoresData, turmas: turmasData } = await firebaseService.getAllData(userProfile.empresaCnpj);
        
        console.log('🔍 Debug - Professores carregados:', professoresData);
        console.log('🔍 Debug - Turmas carregadas:', turmasData);
        
        setProfessores(professoresData);
        setTurmas(turmasData);
        console.log('✅ Dados carregados:', {
          professores: professoresData.length,
          turmas: turmasData.length
        });
      } catch (err) {
        console.error('❌ Erro ao carregar dados:', err);
        setError('Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userProfile?.empresaCnpj]);

  // Filtrar e ordenar professores
  const filteredProfessores = professores
    .filter(professor => {
      const matchesSearch = (professor.nome || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (professor.email || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'todos' || 
                           (statusFilter === 'ativo' && professor.bAtivo) ||
                           (statusFilter === 'inativo' && !professor.bAtivo);
      
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let aValue: string | boolean;
      let bValue: string | boolean;

      switch (sortBy) {
        case 'nome':
          aValue = (a.nome || '').toLowerCase();
          bValue = (b.nome || '').toLowerCase();
          break;
        case 'email':
          aValue = (a.email || '').toLowerCase();
          bValue = (b.email || '').toLowerCase();
          break;
        case 'status':
          aValue = a.bAtivo;
          bValue = b.bAtivo;
          break;
        default:
          aValue = (a.nome || '').toLowerCase();
          bValue = (b.nome || '').toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

  // Estatísticas
  const totalProfessores = professores.length;
  const professoresAtivos = professores.filter(p => p.bAtivo).length;
  const professoresInativos = professores.filter(p => !p.bAtivo).length;

  // Funções de ordenação
  const handleSort = (field: 'nome' | 'email' | 'status') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Função otimizada para obter turmas do professor
  const getTurmasDoProfessor = (professorId: string): Turma[] => {
    if (!professorId) return [];
    
    console.log('🔍 Debug - Professor ID:', professorId);
    console.log('🔍 Debug - Todas as turmas:', turmas);
    
    // Filtrar turmas que contêm o ID do professor
    const turmasDoProfessor = turmas.filter(turma => {
      console.log('🔍 Debug - Turma:', turma.nome, 'professoresIds:', turma.professoresIds);
      
      // Usar apenas o campo correto
      const professoresIds = turma.professoresIds || [];
      const isProfessorInTurma = Array.isArray(professoresIds) && professoresIds.includes(professorId);
      
      console.log('🔍 Debug - Campo usado:', professoresIds, 'Professor na turma:', isProfessorInTurma);
      
      return isProfessorInTurma;
    });
    
    console.log('🔍 Debug - Turmas do professor:', turmasDoProfessor);
    return turmasDoProfessor;
  };

  // Função para formatar horário
  const formatHorario = (inicio: string, fim: string) => {
    if (!inicio || !fim) return 'Horário não definido';
    return `${inicio} - ${fim}`;
  };

  // Função para formatar dias da semana
  const formatDiasSemana = (dias: string[]) => {
    if (!Array.isArray(dias)) return 'Sem dias definidos';
    
    const diasMap: { [key: string]: string } = {
      'monday': 'Seg',
      'tuesday': 'Ter', 
      'wednesday': 'Qua',
      'thursday': 'Qui',
      'friday': 'Sex',
      'saturday': 'Sáb',
      'sunday': 'Dom'
    };
    
    return dias.map(dia => diasMap[dia] || dia).join(', ');
  };

  // Funções de modal
  const handleViewProfessor = (professor: User) => {
    setSelectedProfessor(professor);
    setShowModal(true);
  };

  const handleEditProfessor = (professor: User) => {
    setSelectedProfessor(professor);
    setShowProfessorModal(true);
  };

  const handleNewProfessor = () => {
    setSelectedProfessor(null);
    setShowProfessorModal(true);
  };

  const handleCloseProfessorModal = () => {
    setShowProfessorModal(false);
    setSelectedProfessor(null);
  };

  const handleSaveProfessor = async (professorData: any) => {
    if (!userProfile?.empresaCnpj) return;

    try {
      if (professorData.id) {
        // Atualizar professor existente
        await firebaseService.updateProfessor(userProfile.empresaCnpj, professorData.id, professorData);
        setProfessores(prev => prev.map(p => 
          p.id === professorData.id ? { ...p, ...professorData } : p
        ));
      } else {
        // Criar novo professor
        const professorId = await firebaseService.createProfessor(userProfile.empresaCnpj, professorData);
        // Recarregar dados
        const { professores: professoresData } = await firebaseService.getAllData(userProfile.empresaCnpj);
        setProfessores(professoresData);
      }
      handleCloseProfessorModal();
      showSuccess(
        professorData.id ? 'Professor atualizado' : 'Professor criado',
        professorData.id ? 'Os dados do professor foram atualizados com sucesso.' : 'O professor foi cadastrado com sucesso.'
      );
      return Promise.resolve();
    } catch (error) {
      console.error('Erro ao salvar professor:', error);
      showError('Erro ao salvar', 'Não foi possível salvar o professor. Tente novamente.');
      throw error;
    }
  };

  const handleViewTurma = (turma: Turma) => {
    setSelectedTurma(turma);
    setShowTurmaModal(true);
  };

  const handleToggleStatus = async (professor: User) => {
    if (!userProfile?.empresaCnpj) return;
    
    const action = professor.bAtivo ? 'inativar' : 'ativar';
    
    showConfirm(
      `${action.charAt(0).toUpperCase() + action.slice(1)} professor`,
      `Tem certeza que deseja ${action} o professor ${professor.nome}?`,
      async () => {
    try {
      await firebaseService.updateAlunoStatus(userProfile.empresaCnpj, professor.id, !professor.bAtivo);
      setProfessores(prev => prev.map(p => 
        p.id === professor.id ? { ...p, bAtivo: !p.bAtivo } : p
      ));
          showSuccess(
            `Professor ${action === 'ativar' ? 'ativado' : 'inativado'}`,
            `O professor foi ${action === 'ativar' ? 'ativado' : 'inativado'} com sucesso.`
          );
    } catch (error) {
      console.error('❌ Erro ao atualizar status do professor:', error);
          showError('Erro ao atualizar', 'Não foi possível atualizar o status do professor. Tente novamente.');
        }
    }
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">{error}</div>
        <button 
          onClick={() => window.location.reload()} 
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <NotificationComponent />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <span className="text-gray-500 dark:text-gray-400">Professores</span>
              </li>
              <li>
                <span className="text-gray-400">/</span>
              </li>
              <li>
                <span className="text-gray-900 dark:text-gray-100 font-medium">Todos</span>
              </li>
            </ol>
          </nav>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-2">Todos</h1>
        </div>
        <button 
          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
          onClick={handleNewProfessor}
        >
          <Plus className="h-4 w-4" />
          <span>Novo Professor</span>
        </button>
      </div>

      {/* Summary */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 dark:border-gray-700 p-6">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <span className="font-medium">{filteredProfessores.length}</span> professores encontrados de{' '}
          <span className="font-medium">{totalProfessores}</span> professores cadastrados
          {professoresAtivos > 0 && (
            <> • <span className="font-medium text-green-600">{professoresAtivos}</span> ativos</>
          )}
          {professoresInativos > 0 && (
            <> • <span className="font-medium text-orange-600">{professoresInativos}</span> inativos</>
          )}
        </div>
      </div>

      {/* Search and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
            <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
              placeholder="Procurar por nome ou email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-80"
              />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center space-x-2 px-4 py-2 border rounded-md transition-colors ${
              showFilters || statusFilter !== 'todos'
                ? 'border-green-500 bg-green-50 text-green-700 hover:bg-green-100'
                : 'border-primary-300 text-primary-600 hover:bg-primary-50'
            }`}
          >
            <Filter className="h-4 w-4" />
            <span>Filtros</span>
            {statusFilter !== 'todos' && (
              <span className="ml-1 h-2 w-2 rounded-full bg-green-600"></span>
            )}
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 dark:border-gray-700 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
          >
            <option value="todos">Todos os status</option>
            <option value="ativo">Apenas ativos</option>
            <option value="inativo">Apenas inativos</option>
          </select>
        </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setStatusFilter('todos');
                  setSearchTerm('');
                }}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors"
              >
                Limpar Filtros
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('nome')}
                >
                  <div className="flex items-center gap-1">
                    Professor
                    {sortBy === 'nome' ? (
                      sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 opacity-50" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('email')}
                >
                  <div className="flex items-center gap-1">
                    Contato
                    {sortBy === 'email' ? (
                      sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 opacity-50" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Telefone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Turmas
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Status
                    {sortBy === 'status' ? (
                      sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 opacity-50" />
                    )}
                  </div>
                </th>
                <th className="relative px-6 py-3 w-32">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredProfessores.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <UserCheck className="h-12 w-12 text-gray-400 mb-3" />
                      <p className="text-gray-500 dark:text-gray-400 font-medium">Nenhum professor encontrado</p>
                      <p className="text-sm text-gray-400 mt-1">
                        {searchTerm || statusFilter !== 'todos' 
                          ? 'Tente ajustar os filtros de busca.' 
                          : 'Comece adicionando um novo professor.'
                        }
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredProfessores.map((professor) => (
                  <tr key={professor.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0">
                          {professor.fotoUrl ? (
                            <img 
                              src={professor.fotoUrl} 
                              alt={professor.nome}
                              className="h-10 w-10 rounded-full object-cover"
                            />
                          ) : (
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary-600">
                            {(professor.nome || '').split(' ').map(n => n[0]).join('').substring(0, 2)}
                          </span>
                        </div>
                          )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{professor.nome || 'Sem nome'}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{professor.email || 'Sem email'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">{professor.email || 'Sem email'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">{professor.telefone || 'Sem telefone'}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{professor.cellular || 'Sem celular'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    <div className="flex flex-wrap gap-1">
                      {getTurmasDoProfessor(professor.id).map((turma) => (
                        <button
                          key={turma.id}
                          onClick={() => handleViewTurma(turma)}
                          className="inline-flex items-center gap-x-1 px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors"
                          title={`Clique para ver detalhes da turma ${turma.nome}`}
                        >
                          <Calendar className="h-3 w-3" />
                          {turma.nome || 'Sem nome'}
                        </button>
                      ))}
                      {getTurmasDoProfessor(professor.id).length === 0 && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">Sem turmas</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      professor.bAtivo 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {professor.bAtivo ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-x-1">
                      <button 
                        onClick={() => handleViewProfessor(professor)}
                          className="text-primary-600 hover:text-primary-900 p-2 rounded hover:bg-primary-50 transition-colors"
                        title="Ver detalhes"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleEditProfessor(professor)}
                          className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-gray-100 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
                        title="Editar"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleToggleStatus(professor)}
                          className={`p-2 rounded transition-colors ${
                            professor.bAtivo 
                              ? 'text-orange-600 hover:text-orange-900 hover:bg-orange-50' 
                              : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                          }`}
                        title={professor.bAtivo ? "Inativar" : "Ativar"}
                      >
                        {professor.bAtivo ? <XCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                      </button>
                    </div>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Professor */}
      {showModal && selectedProfessor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
                    {selectedProfessor.fotoUrl ? (
                      <img 
                        src={selectedProfessor.fotoUrl} 
                        alt={selectedProfessor.nome}
                        className="h-12 w-12 rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-lg font-medium text-primary-600">
                        {(selectedProfessor.nome || '').split(' ').map(n => n[0]).join('').substring(0, 2)}
                      </span>
                    )}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Detalhes do Professor</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">ID: {selectedProfessor.id}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>
              </div>
              
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome</label>
                    <p className="text-gray-900 dark:text-gray-100">{selectedProfessor.nome || 'Sem nome'}</p>
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                    <p className="text-gray-900 dark:text-gray-100">{selectedProfessor.email || 'Sem email'}</p>
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Telefone</label>
                    <p className="text-gray-900 dark:text-gray-100">{selectedProfessor.telefone || 'Sem telefone'}</p>
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Celular</label>
                    <p className="text-gray-900 dark:text-gray-100">{selectedProfessor.cellular || 'Sem celular'}</p>
                </div>
                </div>
                
                <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                    selectedProfessor.bAtivo 
                        ? 'bg-green-100 text-green-800 border border-green-200' 
                        : 'bg-red-100 text-red-800 border border-red-200'
                  }`}>
                    {selectedProfessor.bAtivo ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Turmas ({getTurmasDoProfessor(selectedProfessor.id).length})</label>
                  <div className="flex flex-wrap gap-2">
                    {getTurmasDoProfessor(selectedProfessor.id).map((turma) => (
                      <button
                        key={turma.id}
                        onClick={() => {
                          setShowModal(false);
                          handleViewTurma(turma);
                        }}
                          className="inline-flex items-center gap-x-1 px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors border border-blue-200"
                        title={`Clique para ver detalhes da turma ${turma.nome}`}
                      >
                        <Calendar className="h-3 w-3" />
                        {turma.nome || 'Sem nome'}
                      </button>
                    ))}
                    {getTurmasDoProfessor(selectedProfessor.id).length === 0 && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">Nenhuma turma vinculada</span>
                    )}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-x-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button 
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Fechar
                </button>
                <button 
                  onClick={() => {
                    handleEditProfessor(selectedProfessor);
                    setShowModal(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Editar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Turma */}
      {showTurmaModal && selectedTurma && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Detalhes da Turma</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">ID: {selectedTurma.id}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowTurmaModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>
              </div>
              
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome</label>
                    <p className="text-gray-900 dark:text-gray-100">{selectedTurma.nome || 'Sem nome'}</p>
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo</label>
                    <p className="text-gray-900 dark:text-gray-100">{selectedTurma.tipoTurma || 'Tipo não definido'}</p>
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nível</label>
                    <p className="text-gray-900 dark:text-gray-100">{selectedTurma.nivelId || 'Sem nível'}</p>
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Horário</label>
                    <p className="text-gray-900 dark:text-gray-100">{formatHorario(selectedTurma.horarioInicio, selectedTurma.horarioFim)}</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dias da Semana</label>
                    <p className="text-gray-900 dark:text-gray-100">
                    {Array.isArray(selectedTurma.diasSemana) && selectedTurma.diasSemana.length > 0 
                      ? formatDiasSemana(selectedTurma.diasSemana)
                      : 'Sem dias definidos'
                    }
                  </p>
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Capacidade</label>
                    <p className="text-gray-900 dark:text-gray-100">
                    {selectedTurma.alunosIds?.length || 0} / {selectedTurma.limiteAlunos || 0} alunos
                  </p>
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Piscina</label>
                    <p className="text-gray-900 dark:text-gray-100">{selectedTurma.piscina || 'Não definida'}</p>
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                    selectedTurma.ativo 
                        ? 'bg-green-100 text-green-800 border border-green-200' 
                        : 'bg-red-100 text-red-800 border border-red-200'
                  }`}>
                    {selectedTurma.ativo ? 'Ativa' : 'Inativa'}
                  </span>
                  </div>
                </div>
                </div>
                
                {selectedTurma.observacoes && (
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Observações</label>
                  <p className="text-gray-900 dark:text-gray-100">{selectedTurma.observacoes}</p>
                  </div>
                )}
              
              <div className="flex justify-end gap-x-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button 
                  onClick={() => setShowTurmaModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Fechar
                </button>
                <button 
                  onClick={() => {
                    setShowTurmaModal(false);
                    window.location.href = '/turmas';
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Ver na Página de Turmas
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição/Criação de Professor */}
      <ProfessorModal
        professor={selectedProfessor}
        isOpen={showProfessorModal}
        onClose={handleCloseProfessorModal}
        onSave={handleSaveProfessor}
      />
    </div>
  );
};

export default Professores;